import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { stripMarkdown } from '../utils/stripMarkdown';
import { PaperAirplaneIcon, BookOpenIcon, SparklesIcon, MicrophoneIcon, StopCircleIcon } from '@heroicons/react/24/outline';
import MarkdownMessage from '../components/MarkdownMessage';
import VoiceModeOverlay from '../components/VoiceModeOverlay';
import './PublicChat.css';

function slugify(name) {
  return name.trim().replace(/\s+/g, '-').replace(/[/?#\\]/g, '');
}

function PublicChat() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [chatbot, setChatbot] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [chatbotConfig, setChatbotConfig] = useState({});
  const [voiceModeOpen, setVoiceModeOpen] = useState(false);
  // Voice state
  const [recording, setRecording]       = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [speaking, setSpeaking]         = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef   = useRef([]);
  const currentAudioRef  = useRef(null);
  const isRecordingRef   = useRef(false);   // flag to exit the VAD rAF loop
  const audioCtxRef      = useRef(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const storageKey = `chatconv_pub_${id}`;

  const saveConversationId = (convId) => {
    setConversationId(convId);
    if (convId) localStorage.setItem(storageKey, String(convId));
  };

  useEffect(() => {
    axios.get(`/api/chat/${id}/info`)
      .then(r => {
        const bot = r.data.chatbot;
        setChatbot(bot);
        setChatbotConfig(bot.config || {});
        restoreSession();
        // Update URL to include slug (replace so back-button still works)
        navigate(`/chat/${id}/${slugify(bot.name)}`, { replace: true });
        // Set page title
        document.title = `${bot.name} — AI Chat`;
      })
      .catch(() => setNotFound(true));
    return () => { document.title = 'AI Chatbot'; };
  }, [id]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const restoreSession = async () => {
    const savedId = localStorage.getItem(storageKey);
    if (!savedId) return;
    try {
      const res = await axios.get(`/api/chat/${id}/conversation/${savedId}`);
      const history = res.data.messages;
      if (history.length > 0) {
        setMessages(history.map(m => ({ ...m, ts: new Date(m.created_at) })));
        setConversationId(parseInt(savedId, 10));
      }
    } catch {
      localStorage.removeItem(storageKey);
    }
  };

  const handleNewChat = () => {
    localStorage.removeItem(storageKey);
    setConversationId(null);
    setMessages([]);
    inputRef.current?.focus();
  };

  // ── Voice helpers ──────────────────────────────────────────────────────────

  const voiceEnabled = chatbotConfig?.voice_enabled === true;
  const ttsProvider  = chatbotConfig?.tts_provider || 'browser';

  const speakResponse = async (text) => {
    if (ttsProvider === 'none' || !text) return;
    // Stop any currently playing audio
    window.speechSynthesis?.cancel();
    currentAudioRef.current?.pause();

    if (ttsProvider === 'openai') {
      try {
        setSpeaking(true);
        const res = await fetch(`/api/voice/${id}/speak`, {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            text,
            voice: chatbotConfig?.tts_voice || 'nova',
            speed: chatbotConfig?.tts_speed || 1.0,
          }),
        });
        if (!res.ok) throw new Error('TTS request failed');
        const blob  = await res.blob();
        const audio = new Audio(URL.createObjectURL(blob));
        currentAudioRef.current = audio;
        audio.onended = () => setSpeaking(false);
        audio.onerror = () => setSpeaking(false);
        audio.play();
      } catch { setSpeaking(false); }
    } else {
      // Browser SpeechSynthesis
      const utt = new SpeechSynthesisUtterance(text);
      utt.rate  = chatbotConfig?.tts_speed || 1.0;
      utt.onstart = () => setSpeaking(true);
      utt.onend   = () => setSpeaking(false);
      utt.onerror = () => setSpeaking(false);
      window.speechSynthesis.speak(utt);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      const mimeType =
        MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus' :
        MediaRecorder.isTypeSupported('audio/webm')             ? 'audio/webm' :
        MediaRecorder.isTypeSupported('audio/mp4')              ? 'audio/mp4' :
        MediaRecorder.isTypeSupported('audio/ogg;codecs=opus')  ? 'audio/ogg;codecs=opus' :
        '';

      // ── Web Audio VAD ──────────────────────────────────────────────────
      const audioCtx = new AudioContext();
      audioCtxRef.current = audioCtx;
      if (audioCtx.state === 'suspended') await audioCtx.resume();
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 256;
      audioCtx.createMediaStreamSource(stream).connect(analyser);
      const freqData = new Uint8Array(analyser.frequencyBinCount);

      const SPEECH_THRESHOLD  = 20;   // RMS above this = user is speaking
      const SILENCE_THRESHOLD = 15;   // RMS below this (after speech) = silence
      const SILENCE_DURATION  = 2000; // ms of silence before auto-stop
      const MIN_RECORD_MS     = 1000; // never auto-stop sooner than this
      const MAX_RECORD_MS     = 60000; // hard 60 s safety cutoff
      let silenceStart   = null;
      let speechDetected = false; // only auto-stop AFTER user has spoken
      const startedAt    = Date.now();

      // ── MediaRecorder ──────────────────────────────────────────────────
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : {});
      audioChunksRef.current = [];
      recorder.ondataavailable = e => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
      recorder.onstop = () => {
        stream.getTracks().forEach(t => t.stop());
        audioCtx.close();
        const actualMime = recorder.mimeType || mimeType || 'audio/webm';
        transcribeAudio(new Blob(audioChunksRef.current, { type: actualMime }));
      };
      recorder.start();
      mediaRecorderRef.current = recorder;
      isRecordingRef.current   = true;
      setRecording(true);

      // ── Silence detection loop ─────────────────────────────────────────
      const checkSilence = () => {
        if (!isRecordingRef.current) return; // manually stopped — exit loop
        analyser.getByteFrequencyData(freqData);
        const rms = Math.sqrt(freqData.reduce((s, v) => s + v * v, 0) / freqData.length);
        const now = Date.now();

        if (now - startedAt >= MAX_RECORD_MS) { stopRecording(); return; }

        if (rms >= SPEECH_THRESHOLD) {
          // Actively speaking — mark speech detected, reset silence timer
          speechDetected = true;
          silenceStart   = null;
        } else if (speechDetected && rms < SILENCE_THRESHOLD) {
          // Silence after speech — start or advance silence countdown
          if (!silenceStart) silenceStart = now;
          else if (now - silenceStart >= SILENCE_DURATION && now - startedAt >= MIN_RECORD_MS) {
            stopRecording(); return;
          }
        } else {
          // Mid-level noise — reset silence timer, keep waiting
          silenceStart = null;
        }
        requestAnimationFrame(checkSilence);
      };
      requestAnimationFrame(checkSilence);

    } catch (err) {
      alert('Could not start recording: ' + (err.message || 'Unknown error') + '\nPlease allow microphone access in your browser settings.');
    }
  };

  const stopRecording = () => {
    isRecordingRef.current = false;
    mediaRecorderRef.current?.stop();
    setRecording(false);
  };

  const transcribeAudio = async (blob) => {
    setTranscribing(true);
    const controller = new AbortController();
    const timeoutId  = setTimeout(() => controller.abort(), 25000);
    try {
      const fd = new FormData();
      const ext = (blob.type || 'audio/webm').split('/')[1]?.split(';')[0] || 'webm';
      fd.append('audio', blob, `recording.${ext}`);
      const res  = await fetch(`/api/voice/${id}/transcribe`, { method: 'POST', body: fd, signal: controller.signal });
      clearTimeout(timeoutId);
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Transcription failed');
      if (data.text?.trim()) {
        handleSend(null, data.text.trim());
      } else {
        alert("Couldn't hear anything — please try again.");
      }
    } catch (err) {
      clearTimeout(timeoutId);
      alert(err.name === 'AbortError' ? 'Transcription timed out — please try again.' : 'Transcription failed: ' + err.message);
    } finally {
      setTranscribing(false);
    }
  };

  // ── Chat send ──────────────────────────────────────────────────────────────

  const handleSend = async (e, forcedText = null) => {
    if (e) e.preventDefault();
    const text = forcedText || input.trim();
    if (!text || sending) return;
    if (!forcedText) setInput('');
    setSending(true);
    setMessages(prev => [...prev, { role: 'user', content: text, ts: new Date() }]);

    try {
      const res = await axios.post(`/api/chat/${id}`, {
        message: text,
        conversation_id: conversationId,
        user_session_id: localStorage.getItem(`chatses_pub_${id}`) || (() => {
          const sid = 'pub-' + Math.random().toString(36).slice(2);
          localStorage.setItem(`chatses_pub_${id}`, sid);
          return sid;
        })(),
      });
      saveConversationId(res.data.conversation_id);
      const assistantMsg = {
        role: 'assistant',
        content: res.data.message,
        context_used: res.data.context_used || false,
        context_meta: res.data.context_meta || null,
        ts: new Date(),
      };
      setMessages(prev => [...prev, assistantMsg]);
      // Speak response if voice mode was used (i.e. message came from mic)
      if (forcedText) speakResponse(stripMarkdown(res.data.message));
    } catch {
      setMessages(prev => [...prev, { role: 'error', content: 'Failed to get a response. Please try again.', ts: new Date() }]);
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  if (notFound) {
    return (
      <div className="public-chat-error">
        <h2>Chatbot not found</h2>
        <p>This chatbot may have been removed or the link is invalid.</p>
      </div>
    );
  }

  if (!chatbot) return <div className="public-chat-loading">Loading…</div>;

  const sessionId = localStorage.getItem(`chatses_pub_${id}`) || (() => {
    const sid = 'pub-' + Math.random().toString(36).slice(2);
    localStorage.setItem(`chatses_pub_${id}`, sid);
    return sid;
  })();

  return (
    <div className="public-chat">
      {voiceModeOpen && (
        <VoiceModeOverlay
          chatbotId={id}
          chatbotName={chatbot.name}
          chatbotConfig={chatbotConfig}
          conversationId={conversationId}
          sessionId={sessionId}
          onConversationId={saveConversationId}
          onClose={() => setVoiceModeOpen(false)}
        />
      )}
      <header className="public-chat-header">
        <div className="public-chat-avatar">{chatbot.name?.[0]?.toUpperCase() || '🤖'}</div>
        <div className="public-chat-title">
          <h1>{chatbot.name}</h1>
          {chatbot.description && <p>{chatbot.description}</p>}
        </div>
        <div className="public-chat-header-right">
          {voiceEnabled && (
            <button className="btn-voice-mode" onClick={() => setVoiceModeOpen(true)} title="Switch to voice mode">
              🎙 Voice Mode
            </button>
          )}
          {messages.length > 0 && (
            <button className="btn-new-chat-pub" onClick={handleNewChat} title="Start a new conversation">
              + New Chat
            </button>
          )}
          <div className="public-chat-online"><span className="online-dot" /> Online</div>
        </div>
      </header>

      <div className="public-chat-messages">
        {messages.length === 0 && chatbot && (() => {
          const msg = chatbotConfig?.welcome_message !== undefined
            ? chatbotConfig.welcome_message
            : `Hi! I'm ${chatbot.name || 'your assistant'}. 👋 Feel free to look around and ask me anything!`;
          return msg ? (
            <div className="pc-msg pc-msg-assistant">
              <div className="pc-bubble">{msg}</div>
            </div>
          ) : null;
        })()}
        {messages.map((m, i) => (
          <div key={i} className={`pc-msg pc-msg-${m.role}`}>
            <div className="pc-bubble">
              {m.role === 'assistant'
                ? <MarkdownMessage content={m.content} />
                : m.content}
            </div>
            <div className="pc-meta">
              <span className="pc-time">{m.ts?.toLocaleTimeString()}</span>
              {m.role === 'assistant' && (
                <span className={`pc-rag ${m.context_used ? 'rag-hit' : 'rag-miss'}`}
                  title={m.context_meta ? `${m.context_meta.chunks} chunk(s) retrieved` : ''}>
                  {m.context_used
                    ? <><BookOpenIcon className="rag-icon" /> {m.context_meta?.chunks ?? ''} chunk{m.context_meta?.chunks !== 1 ? 's' : ''}</>
                    : <><SparklesIcon className="rag-icon" /> AI Generated</>}
                </span>
              )}
            </div>
            {m.role === 'assistant' && m.context_meta?.sources?.length > 0 && (
              <div className="pc-sources">
                <span className="pc-sources-label">Sources:</span>
                {m.context_meta.sources.map((s, si) => (
                  <a key={si} href={s.url} target="_blank" rel="noopener noreferrer"
                     className="pc-source-link" title={s.url}>
                    {(() => { try { const u = new URL(s.url); return u.hostname + u.pathname.replace(/\/$/, ''); } catch { return s.url; } })()}
                  </a>
                ))}
              </div>
            )}
          </div>
        ))}
        {sending && (
          <div className="pc-msg pc-msg-assistant">
            <div className="pc-bubble pc-typing"><span /><span /><span /></div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <div className="public-chat-input-wrap">
        {(recording || transcribing || speaking) && (
          <div className="voice-status">
            {recording    && <><span className="voice-dot" />Listening…</>}
            {transcribing && <><span className="voice-dot voice-dot-yellow" />Transcribing…</>}
            {speaking     && <><span className="voice-dot voice-dot-green" />Speaking…</>}
          </div>
        )}
        <form className="public-chat-input" onSubmit={handleSend}>
          <input
            ref={inputRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            placeholder={recording ? 'Listening… click mic to stop' : 'Type a message…'}
            disabled={sending || transcribing}
            autoFocus
          />
          {voiceEnabled && (
            <button
              type="button"
              className={`mic-btn ${recording ? 'mic-recording' : ''} ${transcribing ? 'mic-busy' : ''}`}
              onClick={recording ? stopRecording : startRecording}
              disabled={transcribing || sending || speaking}
              title={recording ? 'Stop recording' : 'Voice input (Groq Whisper)'}
            >
              {transcribing
                ? <span className="mic-spinner" />
                : recording
                  ? <StopCircleIcon className="mic-icon" />
                  : <MicrophoneIcon className="mic-icon" />}
            </button>
          )}
          <button type="submit" disabled={!input.trim() || sending}>
            <PaperAirplaneIcon className="send-icon" />
          </button>
        </form>
      </div>
    </div>
  );
}

export default PublicChat;
